import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import GuestLayout from '@/components/guest/GuestLayout.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import RequestStatusBadge from '@/components/guest/RequestStatusBadge.jsx';
import { useSession } from '@/hooks/useSession.js';
import { CheckCircle2, Home, Eye } from 'lucide-react';
import { motion } from 'framer-motion';

const ServiceRequestConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, hotel, room } = useSession();
  const rawRequest = location.state?.request;
  const request = rawRequest
    ? {
        id: rawRequest.id || rawRequest.Id,
        serviceType: rawRequest.serviceType || rawRequest.title || rawRequest.Title || rawRequest.requestTypeName || rawRequest.RequestTypeName,
        quantity: rawRequest.quantity || rawRequest.Quantity || 1,
        roomNumber: rawRequest.roomNumber || rawRequest.RoomNumber || rawRequest.room?.name || rawRequest.Room?.Name || room?.name || room?.Name || "—",
        status: rawRequest.statusName || rawRequest.StatusName || rawRequest.status || rawRequest.Status || "Pending",
        notes: rawRequest.notes || rawRequest.description || rawRequest.Description || "",
      }
    : null;

  if (!request) {
    navigate('/guest/home');
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{`Request Confirmed - ${hotel?.name || 'The Hotelier'}`}</title>
        <meta name="description" content="Your service request has been submitted successfully" />
      </Helmet>

      <GuestLayout hotel={hotel} room={room} session={session}>
        <div className="max-w-2xl mx-auto space-y-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="mx-auto bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold mb-2 text-balance">Request submitted</h1>
            <p className="text-muted-foreground">Your service request has been received</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>Request details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Service type</p>
                    <p className="font-medium">{request.serviceType}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Quantity</p>
                    <p className="font-medium" style={{ fontVariantNumeric: 'tabular-nums' }}>{request.quantity}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Room</p>
                    <p className="font-medium">{request.roomNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Status</p>
                    <RequestStatusBadge status={request.status} />
                  </div>
                </div>

                {request.notes && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm bg-muted p-3 rounded-lg">{request.notes}</p>
                  </div>
                )}

                <div className="bg-accent/30 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Our staff will process your request shortly. You can track the status of your request anytime.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <div className="flex gap-3">
            <Button 
              onClick={() => navigate('/guest/home')} 
              className="flex-1 transition-all duration-200"
            >
              <Home className="w-4 h-4 mr-2" />
              Back to home
            </Button>
            <Button 
              onClick={() => navigate(`/guest/status/${request.id}`)} 
              variant="outline"
              className="flex-1 transition-all duration-200"
            >
              <Eye className="w-4 h-4 mr-2" />
              Track status
            </Button>
          </div>
        </div>
      </GuestLayout>
    </>
  );
};

export default ServiceRequestConfirmationPage;
